import isEqual from "lodash/isEqual";
import * as React from "react";

import { useFileIdToTopics } from "@/shared/components/visualization/WorkspaceCtx/hooks";
import { useVizDispatch } from "@/shared/state/visualization";
import { updateThreeDEntity } from "@/shared/state/visualization/actions";
import { Entity } from "@/shared/state/visualization/schema/v1/three-d";

import { AxisPresetPicker } from "../AxisPresetPicker";

import { positionFor } from "./positionForPreset";
import { PositionPreset, presetFor, presets } from "./presets";
import { useWorkspacePositionPresets } from "./useWorkspacePositionPresets";

/**
 * The preset field of a 3D entity's position settings, where a user picks the
 * coordinate convention and the topic data each axis plots.
 *
 * When no preset matches the entity's current position, the list also offers
 * that position as a "Custom (...)" entry, so the field shows what the entity
 * actually plots instead of sitting blank.
 *
 * Picking an entry replaces the position whole: an axis the picked preset
 * leaves unbound plots 0 rather than keeping the data the entity had.
 */
export function PositionPresetSelector(props: {
  panelId: string;
  entity: Entity;
}) {
  const { panelId, entity } = props;
  const dispatch = useVizDispatch();

  const fileIdToTopics = useFileIdToTopics();

  const prefilled = useWorkspacePositionPresets();
  const [options, currentPreset] = React.useMemo(
    function optionsForPresetsAndCurrentState() {
      const options: PositionPreset[] = [...prefilled];

      function isPositionPreset(preset: PositionPreset) {
        return isEqual(positionFor(preset, fileIdToTopics), entity.position);
      }
      const defaults = Object.values(presets);
      let currentPreset =
        prefilled.find(isPositionPreset) || defaults.find(isPositionPreset);
      // Neither a workspace preset nor a built-in default matches the entity's
      // current position.
      if (!currentPreset) {
        const customPreset = presetFor(entity.position);
        if (customPreset) {
          options.push(customPreset);
        }
        currentPreset = customPreset;
      }

      options.push(...defaults);
      return [options, currentPreset];
    },
    [entity.position, prefilled, fileIdToTopics],
  );

  return (
    <AxisPresetPicker
      options={options}
      value={currentPreset}
      onChange={(preset) => {
        dispatch(
          updateThreeDEntity(panelId, entity.id, {
            position: positionFor(preset, fileIdToTopics),
          }),
        );
      }}
    />
  );
}
